import { AlertCircle, Fingerprint, Plus, Trash2 } from "lucide-react";
import { useState, type FormEvent } from "react";

import { useUmClient } from "../../context/auth-context.js";
import { useCall } from "../../hooks/use-call.js";
import { errorMessage } from "../../lib/error-message.js";
import { formatRelative } from "../../lib/utils.js";
import {
  createPasskey,
  webauthnErrorMessage,
  webauthnSupported,
} from "../../lib/webauthn.js";
import { EmptyState } from "../primitives/empty-state.js";
import { Alert, AlertDescription } from "../ui/alert.js";
import { Button } from "../ui/button.js";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card.js";
import { Input } from "../ui/input.js";
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "../ui/table.js";

/** List, add and remove the signed-in user's passkeys. */
export const PasskeysCard = () => {
  const client = useUmClient();
  const { data, loading, error, refresh } = useCall("listPasskeys");
  const [name, setName] = useState("");
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const supported = webauthnSupported();
  const passkeys = data?.passkeys ?? [];

  const add = async (e: FormEvent) => {
    e.preventDefault();
    setAdding(true);
    setActionError(null);
    try {
      const start = await client.passkeyRegisterStart();
      if (!start.ok) {
        setActionError(errorMessage(start.error, "Couldn't start passkey setup."));
        return;
      }
      const credential = await createPasskey(start.data.options);
      const finish = await client.passkeyRegisterFinish({
        challenge: start.data.challenge,
        response: credential,
        name: name.trim() || undefined,
      });
      if (!finish.ok) {
        setActionError(errorMessage(finish.error, "Couldn't save the passkey."));
        return;
      }
      setName("");
      await refresh();
    } catch (err) {
      // The browser throws when the user cancels the dialog — and with
      // a cryptic InvalidStateError when this device already holds a
      // passkey for the site; translate those into plain language.
      setActionError(webauthnErrorMessage(err, "Passkey setup failed."));
    } finally {
      setAdding(false);
    }
  };

  const remove = async (id: string) => {
    setBusy(id);
    setActionError(null);
    const res = await client.deleteOwnPasskey({ id });
    setBusy(null);
    if (res.ok) {
      await refresh();
    } else {
      setActionError(errorMessage(res.error, "Couldn't remove the passkey."));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Fingerprint className="size-4 text-muted-foreground" />
          Passkeys
        </CardTitle>
        <CardDescription>
          Sign in with your device's fingerprint, face or PIN instead of a
          password.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        {error ? (
          <Alert variant="destructive">
            <AlertCircle />
            <AlertDescription>{errorMessage(error)}</AlertDescription>
          </Alert>
        ) : null}
        {actionError ? (
          <Alert variant="destructive">
            <AlertCircle />
            <AlertDescription>{actionError}</AlertDescription>
          </Alert>
        ) : null}
        {!supported ? (
          <Alert>
            <AlertCircle />
            <AlertDescription>
              This browser doesn't support passkeys.
            </AlertDescription>
          </Alert>
        ) : (
          <form onSubmit={add} className="flex items-end gap-3">
            <div className="grid flex-1 gap-1.5">
              <label
                htmlFor="passkey-name"
                className="text-sm font-medium leading-none"
              >
                Name (optional)
              </label>
              <Input
                id="passkey-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. MacBook Touch ID"
              />
            </div>
            <Button type="submit" loading={adding}>
              <Plus />
              Add passkey
            </Button>
          </form>
        )}

        {loading ? null : passkeys.length === 0 ? (
          <EmptyState
            icon={Fingerprint}
            title="No passkeys yet"
            description="Add one to sign in without a password."
          />
        ) : (
          <div className="overflow-hidden rounded-lg border">
            <Table>
              <TableBody>
                {passkeys.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-medium">
                      {p.name || "Unnamed passkey"}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {p.createdAt ? `Added ${formatRelative(p.createdAt)}` : ""}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => remove(p.id)}
                        loading={busy === p.id}
                        aria-label={`Remove passkey ${p.name ?? p.id}`}
                      >
                        <Trash2 className="text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
